import { Snackbar } from '@material-ui/core';
import Alert, { AlertProps } from '@material-ui/lab/Alert';
import styledFuncs from 'app/styles/styledFuncs';
import styledMixins from 'app/styles/styledMixins';
import useSafeAreaInsets from 'app/tools/hooks/use-safe-area-insets';
import _ from 'lodash';
import { useSubscription } from 'observable-hooks';
import React, { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { Observable } from 'rxjs';
import styled, { css } from 'styled-components/macro';
import Button from '../button/Button';
import Icon from '../icon/Icon';
import IconLib from '../icon/icon-lib';

const StyledAlert = styled(Alert)`
  flex-grow: 1;
  ${styledMixins.roundCorners}
  ${p =>
    p.severity === 'info' &&
    css`
      background: ${p.theme.palette.secondary.main};
    `};
`;

type StyledSnackbarOpts = {
  $fullScreen: boolean;
  $addedBottomMargin: number;
};
const StyledSnackbar = styled(Snackbar)<StyledSnackbarOpts>`
  display: ${p => (p.open ? 'flex' : 'none')};
  margin: ${styledFuncs.pad(0.5)};
  left: ${styledFuncs.pad(0.5)};
  @media (min-width: 600px) {
    margin: 0;
    left: 50%;
    bottom: 30px;
    width: 80%;
    max-width: 600px;
  }
  right: ${styledFuncs.pad(0.5)};
  bottom: ${p => p.theme.spacing(1.5) + 65 + p.$addedBottomMargin}px;

  .MuiPaper-root {
    border: 2px solid white;
  }

  overflow-wrap: break-word;
  hyphens: auto;
`;

const ActionButton = styled(Button)`
  height: auto;
  margin-right: 6px;
  border-radius: 8px;
  min-height: 2.4em;
  align-self: center;

  &,
  &:hover {
    color: white;
    border-color: #fffc;
    border-width: 2px;
    background: #fff3;
  }
`;
const ActionButtonTextWrap = styled.div`
  hyphens: none;
  line-height: 1.3;
  padding: 4px 14px;
`;

const CloseButton = styled(Button).attrs({
  iconButton: { size: 'small' },
})`
  color: white;
  &:hover {
    background: transparent;
  }
`;

export type AppSnackbarOpts = {
  message: string;
  action?: {
    text?: string;
    callback?: () => void;
  };
  alertType?: AlertProps['severity'];
  autoHide?: boolean;
};

type Props = {
  snack$: Observable<AppSnackbarOpts>;
};
export default function AppSnackbar({ snack$ }: Props): JSX.Element {
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [currentSnack, setCurrentSnack] = useState<AppSnackbarOpts>();
  const [snackStack, setSnackStack] = useState<AppSnackbarOpts[]>([]);
  const isFullScreen = useSelector(s => s.context.fullScreen);
  const { bottom: bottomMargin } = useSafeAreaInsets();

  useSubscription(snack$, snack =>
    setSnackStack(s => [...s, { ...snack }])
  );

  useEffect(() => {
    if (snackStack.length && !currentSnack) {
      setCurrentSnack(snackStack[0]);
      setSnackStack(s => s.slice(1));
      setSnackbarOpen(true);
    }
  }, [currentSnack, snackStack]);

  const SnackAction = useMemo(
    () => (
      <>
        {currentSnack?.action?.text && (
          <ActionButton
            onClick={() => {
              currentSnack.action?.callback?.();
              setSnackbarOpen(false);
            }}
            plain
            variant="contained"
          >
            <ActionButtonTextWrap>
              {currentSnack.action.text}
            </ActionButtonTextWrap>
          </ActionButton>
        )}
        <CloseButton
          onClick={() => {
            setSnackbarOpen(false);
          }}
        >
          <Icon icon={IconLib.CROSS} size={22} color="white" />
        </CloseButton>
      </>
    ),
    [currentSnack?.action]
  );

  const autoHideLength = Math.max(
    _.words(currentSnack?.message).length * 650 +
      _.words(currentSnack?.action?.text).length * 2000,
    4000
  );

  const autoHide = currentSnack?.autoHide ?? true;
  const autoHideDuration = autoHide ? autoHideLength : undefined;
  return (
    <StyledSnackbar
      $fullScreen={
        isFullScreen === true || isFullScreen === 'showHeader'
      }
      $addedBottomMargin={bottomMargin * 0.75}
      open={snackbarOpen}
      autoHideDuration={snackStack.length ? 2000 : autoHideDuration}
      onClose={(_e, reason) => {
        if (reason === 'timeout') setSnackbarOpen(false);
      }}
      TransitionProps={{
        onExited: () => setCurrentSnack(undefined),
      }}
    >
      <StyledAlert
        onClose={() => setSnackbarOpen(false)}
        severity={currentSnack?.alertType || 'info'}
        variant="filled"
        action={SnackAction}
      >
        {currentSnack?.message}
      </StyledAlert>
    </StyledSnackbar>
  );
}
